import axios from "axios";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import ConstList from "../utils/ConstList";
import { maintenanceState } from "../stores/MaintenanceAtom";
import { LoginUserAtom } from "../stores/LoginUserAtom";

const ADMIN_AUTHORITY_ID = 1;

/**
 * メンテナンス設定の再取得間隔。
 * 5分ごとにAPIへ問い合わせる。
 */
const MAINTENANCE_FETCH_INTERVAL = 5 * 60 * 1000;

/**
 * 現在時刻の再判定間隔。
 * API通信は行わず、ブラウザ内だけで更新する。
 */
const CURRENT_TIME_INTERVAL = 60 * 1000;

type Props = {
  children: ReactNode;
};

type MaintenanceResponse = {
  id?: number | string;
  display?: number | string | boolean;
  day?: string;
  start_time?: string;
  end_time?: string;
  flag?: number | string | boolean;
};

type ProfileResponse = {
  success?: boolean;
  message?: string;
  data?: {
    user_id?: number | string;
    email?: string;
    name?: string;
    authority_id?: number | string;
    authority_name?: string;
  } | null;
};

function MaintenanceGate({ children }: Props) {
  const [maintenance, setMaintenance] = useRecoilState(maintenanceState);

  const loginUserData = useRecoilValue(LoginUserAtom);

  const [maintenanceLoaded, setMaintenanceLoaded] = useState(false);

  const [currentTime, setCurrentTime] = useState(() => new Date());

  /**
   * メンテナンス中の場合だけ使用する管理者判定。
   */
  const [authorityId, setAuthorityId] = useState<number | null>(null);

  const [authorityChecked, setAuthorityChecked] = useState(false);

  /**
   * メンテナンス設定を取得する。
   */
  const loadMaintenance = useCallback(async () => {
    try {
      const response = await axios.get<MaintenanceResponse>(
        ConstList.getApiUrl() + "GetMaintenance.php",
        ConstList.AXIOS_CONFIG,
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("メンテナンス情報の応答形式が正しくありません。");
      }

      setMaintenance((current: any) => ({
        ...current,
        ...response.data,
      }));
    } catch (caughtError) {
      if (axios.isAxiosError(caughtError)) {
        console.error("メンテナンス情報の取得に失敗しました。", {
          status: caughtError.response?.status,
          responseData: caughtError.response?.data,
          message: caughtError.message,
        });
      } else {
        console.error("メンテナンス情報の取得に失敗しました。", caughtError);
      }

      /*
       * API取得失敗だけでシステム全体を停止させない。
       * Recoilに残っている直前の設定を使用する。
       */
    } finally {
      setMaintenanceLoaded(true);
    }
  }, [setMaintenance]);

  /**
   * 初回取得と5分ごとの再取得。
   */
  useEffect(() => {
    void loadMaintenance();

    const intervalId = window.setInterval(() => {
      void loadMaintenance();
    }, MAINTENANCE_FETCH_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadMaintenance]);

  /**
   * 現在時刻を1分ごとに更新する。
   */
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, CURRENT_TIME_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const isMaintenanceMode = isWithinMaintenanceWindow(maintenance, currentTime);

  /**
   * 通常時は権限APIを呼ばない。
   *
   * メンテナンス中になった時点でのみ、
   * GetMyProfile.phpから管理者権限を確認する。
   */
  useEffect(() => {
    if (!isMaintenanceMode) {
      setAuthorityId(null);
      setAuthorityChecked(false);
      return;
    }

    let cancelled = false;

    const loadAuthority = async () => {
      const email = loginUserData.email?.trim() ?? "";

      if (!email) {
        setAuthorityId(null);
        setAuthorityChecked(true);
        return;
      }

      try {
        setAuthorityChecked(false);

        const response = await axios.post<ProfileResponse>(
          ConstList.getApiUrl() + "GetMyProfile.php",
          {
            email,
          },
          ConstList.AXIOS_CONFIG,
        );

        if (cancelled) {
          return;
        }

        const profile = response.data?.data;

        setAuthorityId(profile ? Number(profile.authority_id ?? 0) : null);
      } catch (caughtError) {
        console.error(
          "ログインユーザーの権限確認に失敗しました。",
          caughtError,
        );

        if (!cancelled) {
          /*
           * 権限確認に失敗した場合は、
           * 安全側として管理者扱いにしない。
           */
          setAuthorityId(null);
        }
      } finally {
        if (!cancelled) {
          setAuthorityChecked(true);
        }
      }
    };

    void loadAuthority();

    return () => {
      cancelled = true;
    };
  }, [isMaintenanceMode, loginUserData.email]);

  /**
   * メンテナンス情報の初回取得中。
   */
  if (!maintenanceLoaded) {
    return <FullScreenLoading />;
  }

  /**
   * 通常時。
   */
  if (!isMaintenanceMode) {
    return <>{children}</>;
  }

  /**
   * メンテナンス中の権限確認待ち。
   */
  if (!authorityChecked) {
    return <FullScreenLoading />;
  }

  /**
   * 管理者は通常画面を使用できる。
   * 画面上部にはメンテナンス警告を表示する。
   */
  if (authorityId === ADMIN_AUTHORITY_ID) {
    return (
      <>
        <AdminMaintenanceNotice
          day={maintenance?.day}
          startTime={maintenance?.start_time}
          endTime={maintenance?.end_time}
          onRefresh={() => {
            void loadMaintenance();
            setCurrentTime(new Date());
          }}
        />

        {children}
      </>
    );
  }

  /**
   * 一般ユーザーはメンテナンス画面だけを表示する。
   */
  return (
    <MaintenanceScreen
      day={maintenance?.day}
      startTime={maintenance?.start_time}
      endTime={maintenance?.end_time}
      onRefresh={() => {
        void loadMaintenance();
        setCurrentTime(new Date());
      }}
    />
  );
}

function isWithinMaintenanceWindow(
  maintenance: MaintenanceResponse,
  currentTime: Date,
): boolean {
  if (!isEnabled(maintenance?.flag)) {
    return false;
  }

  const startDate = createLocalDateTime(
    maintenance?.day ?? "",
    maintenance?.start_time ?? "",
  );

  const endDate = createLocalDateTime(
    maintenance?.day ?? "",
    maintenance?.end_time ?? "",
  );

  /*
   * 日時が不正な場合は、
   * 誤ってシステムを停止させない。
   */
  if (!startDate || !endDate) {
    return false;
  }

  return (
    currentTime.getTime() >= startDate.getTime() &&
    currentTime.getTime() < endDate.getTime()
  );
}

/**
 * DBから返る1、"1"、true、"t"などをONとして扱う。
 */
function isEnabled(value: unknown): boolean {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "t"
  );
}

function createLocalDateTime(day: string, time: string): Date | null {
  const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);

  const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);

  if (!dayMatch || !timeMatch) {
    return null;
  }

  const year = Number(dayMatch[1]);
  const month = Number(dayMatch[2]);
  const date = Number(dayMatch[3]);

  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  const second = Number(timeMatch[3] ?? 0);

  const result = new Date(year, month - 1, date, hour, minute, second);

  if (
    result.getFullYear() !== year ||
    result.getMonth() !== month - 1 ||
    result.getDate() !== date ||
    result.getHours() !== hour ||
    result.getMinutes() !== minute
  ) {
    return null;
  }

  return result;
}

function formatTime(value: unknown): string {
  if (typeof value !== "string" || value.trim() === "") {
    return "未定";
  }

  const parts = value.split(":");

  if (parts.length < 2) {
    return value;
  }

  return `${parts[0]}:${parts[1]}`;
}

function FullScreenLoading() {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

type AdminMaintenanceNoticeProps = {
  day?: string;
  startTime?: string;
  endTime?: string;
  onRefresh: () => void;
};

function AdminMaintenanceNotice({
  day,
  startTime,
  endTime,
  onRefresh,
}: AdminMaintenanceNoticeProps) {
  return (
    <Box
      sx={{
        position: "fixed",
        top: 12,
        left: "50%",
        zIndex: (theme) => theme.zIndex.snackbar + 1,
        width: {
          xs: "calc(100% - 24px)",
          md: "auto",
        },
        minWidth: {
          md: 600,
        },
        maxWidth: 900,
        transform: "translateX(-50%)",
      }}
    >
      <Alert
        severity="warning"
        variant="filled"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={onRefresh}
            sx={{
              whiteSpace: "nowrap",
              fontWeight: 700,
            }}
          >
            再確認
          </Button>
        }
        sx={{
          alignItems: "center",
          borderRadius: 2,
          boxShadow: 4,
        }}
      >
        <AlertTitle sx={{ fontWeight: 800 }}>現在メンテナンス中です</AlertTitle>
        管理者としてアクセスしています。 一般ユーザーは現在利用できません。
        {"　"}
        実施日時：{day || "未定"} {formatTime(startTime)}～{formatTime(endTime)}
      </Alert>
    </Box>
  );
}

type MaintenanceScreenProps = {
  day?: string;
  startTime?: string;
  endTime?: string;
  onRefresh: () => void;
};

function MaintenanceScreen({
  day,
  startTime,
  endTime,
  onRefresh,
}: MaintenanceScreenProps) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: {
              xs: 3,
              sm: 5,
            },
            borderRadius: 3,
          }}
        >
          <Stack spacing={3}>
            <Alert severity="warning">
              <AlertTitle>システムメンテナンス中です</AlertTitle>
              現在、システムをご利用いただけません。
            </Alert>

            <Stack spacing={1}>
              <Typography>実施日：{day || "未定"}</Typography>

              <Typography>
                予定時間：
                {formatTime(startTime)}～{formatTime(endTime)}
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              メンテナンス終了後、最大1分程度で通常画面へ戻ります。
            </Typography>

            <Button variant="outlined" onClick={onRefresh}>
              メンテナンス状態を再確認
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default MaintenanceGate;
